import { HttpContextToken, HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, switchMap, take, throwError } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { TokenService } from '../services/token.service';
import { SessionTimeoutService } from '../services/session-timeout.service';

/** Set on the retried request so a second 401 does not trigger another refresh loop. */
export const AUTH_REFRESH_RETRY_CONTEXT = new HttpContextToken<boolean>(() => false);

const SKIP_REFRESH_FOR_URL_PARTS = [
  '/auth/admin-login',
  '/auth/login',
  '/auth/forgot-password',
  '/auth/reset-password',
  '/auth/refresh',
  '/auth/logout',
];

function shouldAttemptRefresh(reqUrl: string): boolean {
  return !SKIP_REFRESH_FOR_URL_PARTS.some((part) => reqUrl.includes(part));
}

export const authRefreshInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(AuthService);
  const tokens = inject(TokenService);
  const sessionTimeout = inject(SessionTimeoutService);

  return next(req).pipe(
    catchError((error: unknown) => {
      if (!(error instanceof HttpErrorResponse) || error.status !== 401) {
        return throwError(() => error);
      }
      if (req.context.get(AUTH_REFRESH_RETRY_CONTEXT)) {
        return throwError(() => error);
      }
      if (!shouldAttemptRefresh(req.url)) {
        return throwError(() => error);
      }

      return auth.refreshToken().pipe(
        take(1),
        switchMap(() => {
          const newToken = tokens.getToken();

          if (!newToken) {
            return throwError(() => error);
          }

          sessionTimeout.start();

          const retry = req.clone({
            setHeaders: {
              Authorization: `Bearer ${newToken}`,
            },
            context: req.context.set(AUTH_REFRESH_RETRY_CONTEXT, true),
          });

          return next(retry);
        }),
      );
    }),
  );
};
